import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Users, Calendar, FileText, Bell, Search, RefreshCw, ArrowLeft,
  CheckCircle2, Clock, AlertTriangle, Building2, Activity, Eye
} from "lucide-react";

// Mock data for partner portal
const MOCK_PATIENTS = [
  { id: "P001", name: "Mary Johnson", referralDate: "2026-01-15", status: "active", diagnosis: "End-stage COPD", lastVisit: "2026-01-18" },
  { id: "P002", name: "Robert Smith", referralDate: "2026-01-10", status: "active", diagnosis: "Metastatic Lung Cancer", lastVisit: "2026-01-17" },
  { id: "P003", name: "Patricia Williams", referralDate: "2026-01-08", status: "discharged", diagnosis: "End-stage Heart Failure", lastVisit: "2026-01-12" },
  { id: "P004", name: "James Brown", referralDate: "2026-01-05", status: "active", diagnosis: "Advanced Dementia", lastVisit: "2026-01-16" },
];

const MOCK_SCHEDULE = [
  { id: "V001", patient: "Mary Johnson", type: "RN Visit", date: "2026-01-21", time: "09:00 AM", staff: "Jennifer Adams" },
  { id: "V002", patient: "Robert Smith", type: "SW Visit", date: "2026-01-21", time: "11:00 AM", staff: "Lisa Thompson" },
  { id: "V003", patient: "James Brown", type: "Aide Visit", date: "2026-01-22", time: "10:00 AM", staff: "Amanda Rodriguez" },
];

const MOCK_FOLLOWUPS = [
  { id: "F001", patient: "Mary Johnson", reason: "Medication change request", priority: "high", created: "2026-01-18" },
  { id: "F002", patient: "Robert Smith", reason: "Order renewal needed", priority: "medium", created: "2026-01-17" },
];

const MOCK_DOCUMENTS = [
  { id: "D001", name: "IDG Meeting Notes - Week 3", type: "report", date: "2026-01-19", from: "Goodwin Cares" },
  { id: "D002", name: "Mary Johnson - Care Plan Update", type: "care_plan", date: "2026-01-18", from: "RN Case Manager" },
  { id: "D003", name: "Monthly Compliance Report", type: "compliance", date: "2026-01-15", from: "Quality Team" },
];

export default function PartnerPortal() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("patients");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Data
  const [patients, setPatients] = useState(MOCK_PATIENTS);
  const [schedule, setSchedule] = useState(MOCK_SCHEDULE);
  const [followups, setFollowups] = useState(MOCK_FOLLOWUPS);
  const [documents, setDocuments] = useState(MOCK_DOCUMENTS);
  
  // Dialogs
  const [showPatientsDialog, setShowPatientsDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showFollowupsDialog, setShowFollowupsDialog] = useState(false);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [dialogItems, setDialogItems] = useState([]);
  const [dialogTitle, setDialogTitle] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Try to load real data, fall back to mock
      const [patientsRes, scheduleRes] = await Promise.all([
        api.get("/referrals/partner/patients").catch(() => ({ data: { patients: MOCK_PATIENTS } })),
        api.get("/scheduling/partner/visits").catch(() => ({ data: { visits: MOCK_SCHEDULE } }))
      ]);
      
      setPatients(patientsRes.data?.patients || MOCK_PATIENTS);
      setSchedule(scheduleRes.data?.visits || MOCK_SCHEDULE);
    } catch (error) {
      console.error("Load error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatClick = (type, items, title) => {
    setDialogItems(items);
    setDialogTitle(title);
    switch(type) {
      case 'patients': setShowPatientsDialog(true); break;
      case 'schedule': setShowScheduleDialog(true); break;
      case 'followups': setShowFollowupsDialog(true); break;
      case 'documents': setShowDocumentsDialog(true); break;
    }
  };

  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="partner-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-white hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                <Building2 className="h-8 w-8" />
                Partner Portal
              </h1>
              <p className="text-blue-100 mt-1">View your referred patients and coordinate care</p>
            </div>
            <Button onClick={loadData} className="bg-white/10 hover:bg-white/20">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleStatClick('patients', patients, 'Referred Patients')}
            data-testid="stat-patients"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-700">{patients.filter(p => p.status === 'active').length}</p>
                  <p className="text-sm text-muted-foreground">Assigned Patients</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <p className="text-xs text-blue-500 mt-2">View referred patients →</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleStatClick('schedule', schedule, 'This Week\'s Schedule')}
            data-testid="stat-schedule"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-700">{schedule.length}</p>
                  <p className="text-sm text-muted-foreground">Active This Week</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-xs text-green-500 mt-2">View schedule →</p>
            </CardContent>
          </Card>
          
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${followups.length > 0 ? "border-amber-200 bg-amber-50" : ""}`}
            onClick={() => handleStatClick('followups', followups, 'Follow-ups Needed')}
            data-testid="stat-followups"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-amber-700">{followups.length}</p>
                  <p className="text-sm text-muted-foreground">Need Follow-up</p>
                </div>
                <Bell className="w-8 h-8 text-amber-500" />
              </div>
              <p className="text-xs text-amber-500 mt-2">View follow-ups →</p>
            </CardContent>
          </Card>
          
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleStatClick('documents', documents, 'Documents')}
            data-testid="stat-documents"
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-700">{documents.length}</p>
                  <p className="text-sm text-muted-foreground">Documents</p>
                </div>
                <FileText className="w-8 h-8 text-purple-500" />
              </div>
              <p className="text-xs text-purple-500 mt-2">View documents →</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="patients"><Users className="w-4 h-4 mr-2" />Patients</TabsTrigger>
            <TabsTrigger value="schedule"><Calendar className="w-4 h-4 mr-2" />Schedule</TabsTrigger>
            <TabsTrigger value="documents"><FileText className="w-4 h-4 mr-2" />Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="patients">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Referred Patients</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {filteredPatients.map(patient => (
                    <div key={patient.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div>
                        <p className="font-medium">{patient.name}</p>
                        <p className="text-sm text-muted-foreground">{patient.diagnosis}</p>
                        <p className="text-xs text-muted-foreground">Referred: {patient.referralDate}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={patient.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {patient.status}
                        </Badge>
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="schedule">
            <Card>
              <CardHeader>
                <CardTitle>This Week's Schedule</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {schedule.map(visit => (
                    <div key={visit.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div>
                        <p className="font-medium">{visit.patient}</p>
                        <p className="text-sm text-muted-foreground">{visit.type} - {visit.staff}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{visit.date}</p>
                        <p className="text-sm text-muted-foreground">{visit.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle>Shared Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-500" />
                        <div>
                          <p className="font-medium">{doc.name}</p>
                          <p className="text-sm text-muted-foreground">From: {doc.from}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-muted-foreground">{doc.date}</span>
                        <Button size="sm" variant="outline">Download</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Patients Dialog */}
      <Dialog open={showPatientsDialog} onOpenChange={setShowPatientsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {dialogItems.map((patient, idx) => (
              <div key={idx} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{patient.name}</p>
                    <p className="text-sm text-muted-foreground">{patient.diagnosis}</p>
                  </div>
                  <Badge className={patient.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                    {patient.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-ups Dialog */}
      <Dialog open={showFollowupsDialog} onOpenChange={setShowFollowupsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Follow-ups Needed</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {followups.map((item, idx) => (
              <div key={idx} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{item.patient}</p>
                    <p className="text-sm text-muted-foreground">{item.reason}</p>
                  </div>
                  <Badge className={item.priority === 'high' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                    {item.priority}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={showDocumentsDialog} onOpenChange={setShowDocumentsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Documents</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {documents.map((doc, idx) => (
              <div key={idx} className="p-4 border rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-blue-500" />
                  <div>
                    <p className="font-medium">{doc.name}</p>
                    <p className="text-sm text-muted-foreground">{doc.date}</p>
                  </div>
                </div>
                <Button size="sm" variant="outline">Download</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
